/**
 * Utility functions for deal evaluation using the same thresholds as the "make a wish" functionality
 */

export type DealType = "none" | "bad" | "poor" | "fair" | "good" | "great";

/**
 * Evaluate deal type based on listing price vs market average
 * Uses the same thresholds as wish_matcher_service.py:
 * - GREAT deal: 20% below market average (0.8 * avg_price)
 * - GOOD deal: 10% below market average (0.9 * avg_price)
 * - FAIR deal: 10% above market average (1.1 * avg_price)
 * - POOR deal: 20% above market average (1.2 * avg_price)
 * - BAD deal: More than 20% above market average (>1.2 * avg_price)
 */
export const evaluateDealType = (priceRatio: number): DealType => {
  if (!priceRatio) {
    return "none";
  }
  // flip the sign; when sorting by price_ratio in mongo, nulls are at the end
  if (priceRatio < 0) {
    priceRatio = priceRatio * -1;
  }

  // Great deal: 20% below market or more
  if (priceRatio <= 0.8) {
    return "great";
  }

  // Good deal: 10% below market (but not great)
  if (priceRatio <= 0.9) {
    return "good";
  }

  // Fair deal: within 10% above market
  if (priceRatio < 1.1) {
    return "fair";
  }

  // Poor deal: 10-20% above market
  if (priceRatio < 1.2) {
    return "poor";
  }

  // Bad deal: more than 20% above market
  return "bad";
};

/**
 * Calculate standardized percentage difference for consistent display across components
 * Uses 1 decimal place for precision while maintaining readability
 */
export const calculatePercentageDifference = (listingPrice: number, marketAverage: number, precision: number = 1) => {
    if (!listingPrice || !marketAverage || marketAverage <= 0) {
        return null;
    }

    const difference = listingPrice - marketAverage;
    const percentDifference = (difference / marketAverage) * 100;

    return {
        percentDifference: Number(percentDifference.toFixed(precision)),
        dollarDifference: difference,
        isAboveMarket: difference >= 0
    };
};
